/**
 * Servicios para gestión de eventos
 */

import {
  where,
  orderBy,
  Timestamp,
  runTransaction,
  doc,
  increment,
  serverTimestamp,
  deleteField,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import {
  getById,
  getAll,
  create,
  update,
  remove,
  subscribeToDocument,
  subscribeToCollection,
} from "./base";
import { uploadEncryptedReceipt, deleteFile } from "./storage";

// ID determinístico para clubEventRankings: previene race conditions de
// duplicados cuando dos inscripciones del mismo club se procesan en paralelo.
const rankingDocId = (eventId: string, clubId: string) =>
  `${eventId}_${clubId}`;

// Estados de inscripción que "cuentan": para el ranking del club, para los
// contadores del evento y para saber si alguien ya ocupó su cupo en otro
// evento (F13). Una inscripción cancelada o rechazada no cuenta para nada.
export const ACTIVE_REG_STATUSES: ReadonlyArray<RegistrationStatus> = [
  "pending",
  "approved",
  "waitlist",
];
const isActiveStatus = (s: RegistrationStatus) =>
  ACTIVE_REG_STATUSES.includes(s);

/**
 * Wrapper de retry para `runTransaction`. El SDK web reintenta internamente
 * solo unas pocas veces y, bajo alta contención (ej: 10 inscripciones del
 * mismo club golpeando el mismo doc de ranking en paralelo), se agota y
 * lanza FAILED_PRECONDITION. Esto añade reintentos a nivel de aplicación
 * con backoff exponencial + jitter.
 *
 * IMPORTANTE: solo se reintentan errores de contención. Errores de negocio
 * (ej: "Duplicado", "Evento cerrado") se propagan inmediatamente.
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  opts: { maxAttempts?: number; baseMs?: number; label?: string } = {},
): Promise<T> {
  const max = opts.maxAttempts ?? 6;
  const base = opts.baseMs ?? 50;
  let lastErr: unknown;
  for (let attempt = 1; attempt <= max; attempt++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      const msg = e instanceof Error ? e.message : String(e);
      const isContention =
        msg.includes("FAILED_PRECONDITION") ||
        msg.includes("ABORTED") ||
        msg.includes("the stored version") ||
        msg.includes("aborted") ||
        msg.includes("contention") ||
        msg.includes("Too much contention");
      if (!isContention || attempt === max) throw e;
      const delay = Math.floor(
        base * 2 ** (attempt - 1) * (0.5 + Math.random()),
      );
      if (opts.label) {
        console.warn(
          `[${opts.label}] retry ${attempt}/${max - 1} tras contención (${delay}ms): ${msg}`,
        );
      }
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr;
}
import type {
  Event,
  EventRegistration,
  EventPayment,
  EventPaymentStatus,
  EventStatus,
  RegistrationStatus,
  ClubEventRanking,
  EventRegistrationAudit,
  RegistrationAuditAction,
  RegistrationAuditChange,
} from "@/types";

// ==================== EVENTS ====================

export const eventService = {
  async getById(id: string): Promise<Event | null> {
    return getById<Event>("events", id);
  },

  async getAll(): Promise<Event[]> {
    return getAll<Event>("events", [orderBy("startDate", "desc")]);
  },

  async getByStatus(status: EventStatus): Promise<Event[]> {
    return getAll<Event>("events", [
      where("status", "==", status),
      orderBy("startDate", "desc"),
    ]);
  },

  async getPublished(): Promise<Event[]> {
    return getAll<Event>("events", [
      where("status", "in", [
        "published",
        "registration_closed",
        "in_progress",
      ]),
      orderBy("startDate", "desc"),
    ]);
  },

  async getUpcoming(): Promise<Event[]> {
    const now = Timestamp.now();
    return getAll<Event>("events", [
      where("status", "in", ["published", "registration_closed"]),
      where("startDate", ">=", now),
      orderBy("startDate", "asc"),
    ]);
  },

  async getByOrganizer(organizerId: string): Promise<Event[]> {
    return getAll<Event>("events", [
      where("organizerId", "==", organizerId),
      orderBy("startDate", "desc"),
    ]);
  },

  async getByLevel(level: Event["level"]): Promise<Event[]> {
    return getAll<Event>("events", [
      where("level", "==", level),
      orderBy("startDate", "desc"),
    ]);
  },

  async create(
    data: Omit<
      Event,
      "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
    >,
    userId: string,
  ): Promise<string> {
    return create<Event>("events", data, userId);
  },

  async update(id: string, data: Partial<Event>): Promise<void> {
    return update<Event>("events", id, data);
  },

  async updateStatus(id: string, status: EventStatus): Promise<void> {
    return update<Event>("events", id, { status });
  },

  async delete(id: string): Promise<void> {
    return remove("events", id);
  },

  // Los contadores de inscritos (registeredCount/confirmedCount) ya no se
  // escriben desde el cliente: los mantiene el trigger
  // `onEventRegistrationWrite`, porque las rules solo dejan a un admin tocar
  // los eventos de la Asociación.
};

// ==================== EVENT REGISTRATIONS ====================

export const eventRegistrationService = {
  async getById(id: string): Promise<EventRegistration | null> {
    return getById<EventRegistration>("eventRegistrations", id);
  },

  async getByEvent(eventId: string): Promise<EventRegistration[]> {
    return getAll<EventRegistration>("eventRegistrations", [
      where("eventId", "==", eventId),
      orderBy("registrationDate", "desc"),
    ]);
  },

  async getByEventAndStatus(
    eventId: string,
    status: RegistrationStatus,
  ): Promise<EventRegistration[]> {
    return getAll<EventRegistration>("eventRegistrations", [
      where("eventId", "==", eventId),
      where("status", "==", status),
      orderBy("registrationDate", "desc"),
    ]);
  },

  async getByClub(clubId: string): Promise<EventRegistration[]> {
    return getAll<EventRegistration>("eventRegistrations", [
      where("clubId", "==", clubId),
      orderBy("registrationDate", "desc"),
    ]);
  },

  async getByClubAndEvent(
    clubId: string,
    eventId: string,
  ): Promise<EventRegistration[]> {
    return getAll<EventRegistration>("eventRegistrations", [
      where("clubId", "==", clubId),
      where("eventId", "==", eventId),
      orderBy("registrationDate", "desc"),
    ]);
  },

  async getByPerson(personId: string): Promise<EventRegistration[]> {
    return getAll<EventRegistration>("eventRegistrations", [
      where("personId", "==", personId),
      orderBy("registrationDate", "desc"),
    ]);
  },

  async create(
    data: Omit<
      EventRegistration,
      "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
    >,
    userId: string,
    options?: {
      /**
       * Si true, omite las validaciones de fecha de inscripción
       * (startDate/endDate). Pensado para que un admin de asociación/unión
       * pueda inscribir miembros DESPUÉS del cierre (casos de último momento).
       * El caller (UI) sólo debe pasarlo cuando el actor es admin; las
       * firestore.rules no validan fechas, así que el control de quién puede
       * usarlo vive en la UI por rol.
       */
      skipDateChecks?: boolean;
    },
  ): Promise<string> {
    // ID determinístico: previene duplicados en races paralelos.
    const registrationId = `${data.eventId}_${data.personId}`;
    const regRef = doc(db, "eventRegistrations", registrationId);
    const rankingRef = doc(
      db,
      "clubEventRankings",
      rankingDocId(data.eventId, data.clubId),
    );

    // === Validación pre-TX (no transaccional) ===
    // Leemos el evento UNA vez fuera de la TX. Esto es eventually consistent
    // pero las firestore.rules son la última línea de defensa server-side.
    // No leerlo dentro de la TX evita serialización cuando 100+ clubes
    // inscriben en paralelo al mismo evento (todos compiten por el lock del
    // doc events/{id}).
    const eventPreSnap = await getById<Event>("events", data.eventId);
    if (!eventPreSnap) throw new Error("Evento no encontrado");
    if (!["published", "in_progress"].includes(eventPreSnap.status)) {
      throw new Error("El evento no está abierto para inscripciones");
    }
    const cfg = eventPreSnap.registrationConfig;
    const nowMs = Date.now();
    // Bug fix 2026-04-26: getById() pasa por convertTimestamps() y devuelve
    // Date en lugar de Timestamp, por lo que `.toMillis()` no existe.
    // Usamos un helper tolerante que acepta Timestamp | Date | string | number.
    const toMs = (v: unknown): number | null => {
      if (!v) return null;
      if (typeof v === "number") return v;
      if (v instanceof Date) return v.getTime();
      if (typeof v === "object" && v !== null) {
        const anyV = v as { toMillis?: () => number; seconds?: number };
        if (typeof anyV.toMillis === "function") return anyV.toMillis();
        if (typeof anyV.seconds === "number") return anyV.seconds * 1000;
      }
      if (typeof v === "string") {
        const t = Date.parse(v);
        return Number.isNaN(t) ? null : t;
      }
      return null;
    };
    // Reapertura para UN club: la Asociación mete su id en
    // `registrationOpenClubIds` y ese club vuelve a inscribir aunque la ventana
    // del evento esté cerrada. A diferencia de `skipDateChecks` —que es un
    // permiso del ACTOR y lo decide la UI por rol— esto es un permiso del CLUB
    // y lo respaldan las firestore.rules, así que el director lo usa solo.
    const clubReabierto = (
      eventPreSnap.registrationOpenClubIds ?? []
    ).includes(data.clubId);

    // Validaciones de fecha: se omiten cuando el actor es admin (skipDateChecks)
    // para permitir inscripción de último momento tras el cierre.
    if (!options?.skipDateChecks && !clubReabierto) {
      const startMs = toMs(cfg?.startDate);
      const endMs = toMs(cfg?.endDate);
      if (startMs !== null && nowMs < startMs) {
        throw new Error(
          "Las inscripciones aún no están abiertas para este evento",
        );
      }
      if (endMs !== null && nowMs > endMs) {
        throw new Error("Las inscripciones ya están cerradas para este evento");
      }
    }

    await withRetry(
      () =>
        runTransaction(db, async (tx) => {
          // === LECTURAS (Firestore TX exige reads antes de writes) ===
          // Nota: NO leemos el evento en la TX para evitar serialización
          // cuando 100+ clubes inscriben en paralelo al mismo evento. Los
          // contadores agregados los lleva el trigger del servidor.
          const [existing, rankingSnap] = await Promise.all([
            tx.get(regRef),
            tx.get(rankingRef),
          ]);

          // Hot-fix 2026-04-27 (FIEL CONQUISTADOR):
          // El ID de la inscripción es determinístico `${eventId}_${personId}`.
          // Cuando un director "elimina" una inscripción desde la UI de club,
          // en realidad sólo se hace soft-delete (status='cancelled'). Si luego
          // intenta volver a inscribir a la misma persona (típicamente para
          // corregir el rol/cargo), el doc seguía existiendo y se lanzaba
          // "ya está inscrito(a)". Permitimos REACTIVAR inscripciones que
          // estén en estado terminal (cancelled / rejected) reescribiendo los
          // campos editables y reaplicando contadores/ranking.
          const TERMINAL_STATUSES = new Set(["cancelled", "rejected"]);
          let isReactivation = false;
          let previousStatus: RegistrationStatus | undefined;
          if (existing.exists()) {
            const prev = existing.data() as EventRegistration;
            previousStatus = prev.status;
            if (!TERMINAL_STATUSES.has(prev.status)) {
              throw new Error(
                `${data.personName} ya está inscrito(a) en este evento`,
              );
            }
            isReactivation = true;
          }

          const counts = isActiveStatus(data.status);

          // === ESCRITURAS ===
          if (isReactivation) {
            // Update preserva createdAt/createdBy (las firestore.rules exigen
            // inmutabilidad). Sobrescribe el resto con los nuevos datos.
            const {
              eventId: _eId,
              clubId: _cId,
              personId: _pId,
              ...editable
            } = data as EventRegistration;
            void _eId;
            void _cId;
            void _pId;
            tx.update(regRef, {
              ...editable,
              status: data.status,
              updatedAt: serverTimestamp(),
              updatedBy: userId,
            });
          } else {
            tx.set(regRef, {
              ...data,
              id: registrationId,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              createdBy: userId,
              updatedBy: userId,
            });
          }

          // Bug 1+2+6: actualizar ranking ATÓMICAMENTE en la misma TX.
          // Sin queries de conteo (eventual consistency) y sin client-side now().
          // Para reactivaciones desde estado terminal: el delta es +1 si el
          // nuevo estado es activo (porque al cancelar/rechazar se restó -1).
          void previousStatus;
          if (counts) {
            if (!rankingSnap.exists()) {
              tx.set(rankingRef, {
                id: rankingDocId(data.eventId, data.clubId),
                eventId: data.eventId,
                clubId: data.clubId,
                clubName: data.clubName,
                totalRegistered: 1,
                totalPayments: 0,
                totalPaymentAmount: 0,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                createdBy: userId,
                updatedBy: userId,
              });
            } else {
              const upd: Record<string, unknown> = {
                totalRegistered: increment(1),
                updatedAt: serverTimestamp(),
                updatedBy: userId,
              };
              // El sello (completedAt) lo pone el servidor: ver
              // functions/src/ranking-seal.ts. El cliente sólo lleva los
              // totales para que la UI reaccione al instante.
              tx.update(rankingRef, upd);
            }
          }
        }),
      { label: "createRegistration" },
    );

    // Los contadores agregados del evento (registeredCount/confirmedCount) los
    // mantiene el trigger `onEventRegistrationWrite`. Antes se incrementaban
    // aquí con un updateDoc post-TX "no bloqueante", pero en los eventos de la
    // ASOCIACIÓN las rules solo permiten update a un admin: el incremento del
    // director se rechazaba y el .catch() lo tragaba (contador congelado).

    return registrationId;
  },

  async update(id: string, data: Partial<EventRegistration>): Promise<void> {
    return update<EventRegistration>("eventRegistrations", id, data);
  },

  async updateStatus(
    id: string,
    status: RegistrationStatus,
    approvedBy?: string,
    rejectionReason?: string,
  ): Promise<void> {
    const regRef = doc(db, "eventRegistrations", id);

    await withRetry(
      () =>
        runTransaction(db, async (tx) => {
          // === LECTURAS ===
          const regSnap = await tx.get(regRef);
          if (!regSnap.exists()) throw new Error("Registro no encontrado");
          const registration = regSnap.data() as EventRegistration;
          const previousStatus = registration.status;

          const rankingRef = doc(
            db,
            "clubEventRankings",
            rankingDocId(registration.eventId, registration.clubId),
          );
          const rankingSnap = await tx.get(rankingRef);

          // === DELTAS === (solo para el ranking del club; los contadores
          // agregados del evento los lleva el trigger del servidor)
          const wasActive = isActiveStatus(previousStatus);
          const isNowActive = isActiveStatus(status);
          let registeredDelta = 0;
          if (wasActive && !isNowActive) registeredDelta = -1;
          else if (!wasActive && isNowActive) registeredDelta = 1;

          // === ESCRITURAS ===
          const updateData: Record<string, unknown> = {
            status,
            updatedAt: serverTimestamp(),
          };
          if (approvedBy) updateData.updatedBy = approvedBy;
          if (status === "approved" && approvedBy) {
            updateData.approvalDate = serverTimestamp();
            updateData.approvedBy = approvedBy;
          }
          if (status === "rejected") {
            updateData.rejectedAt = serverTimestamp();
            if (approvedBy) updateData.rejectedBy = approvedBy;
            if (rejectionReason) updateData.rejectionReason = rejectionReason;
          }
          tx.update(regRef, updateData);

          // Contadores del EVENTO se actualizan POST-TX (ver abajo) para evitar
          // contención cuando muchas aprobaciones ocurren en paralelo.

          // Bug 1+2+6: ranking ATÓMICO en la misma TX (particionado por club,
          // no causa contención global).
          if (registeredDelta !== 0) {
            if (!rankingSnap.exists()) {
              // Edge: cambio de status sobre un registro cuyo ranking nunca
              // se creó. Solo crear si el nuevo estado es activo (delta +1).
              if (registeredDelta > 0) {
                tx.set(rankingRef, {
                  id: rankingDocId(registration.eventId, registration.clubId),
                  eventId: registration.eventId,
                  clubId: registration.clubId,
                  clubName: registration.clubName,
                  totalRegistered: 1,
                  totalPayments: 0,
                  totalPaymentAmount: 0,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                  createdBy: approvedBy ?? registration.registeredById,
                  updatedBy: approvedBy ?? registration.registeredById,
                });
              }
            } else {
              const upd: Record<string, unknown> = {
                totalRegistered: increment(registeredDelta),
                updatedAt: serverTimestamp(),
                updatedBy: approvedBy ?? registration.registeredById,
              };
              // El sello (completedAt) lo pone el servidor: ver
              // functions/src/ranking-seal.ts. El cliente sólo lleva los
              // totales para que la UI reaccione al instante.
              // El puesto en el ranking NO se pierde aunque el club baje de
              // 10 inscritos: es un requerimiento de la Asociación (bug 7),
              // reconfirmado el 2026-08-06 al blindar el campamento. Parece un
              // descuido cuando uno ve el escenario "me sello y después
              // cancelo", pero es deliberado: quien llegó primero, llegó.
              // Lo que sí se endureció es la puerta de entrada — el sello
              // exige ahora un pago creíble (ver uploadPayment).
              tx.update(rankingRef, upd);
            }
          }
        }),
      { label: "updateRegistrationStatus" },
    );

    // Los contadores del evento los mantiene `onEventRegistrationWrite`
    // (el cliente no puede escribirlos en eventos de la Asociación).
  },

  async cancel(id: string): Promise<void> {
    const registration = await this.getById(id);
    if (!registration) throw new Error("Registro no encontrado");

    // updateStatus ya maneja la actualización de contadores
    await this.updateStatus(id, "cancelled");
  },

  /**
   * Intercambia un integrante por otro dentro del MISMO club, en una sola
   * operación atómica. El integrante que ENTRA (`incoming`) hereda precio,
   * estado de pago, monto pagado, categoría y estado de la inscripción del que
   * SALE (`outgoingRegistrationId`). El cupo pagado se cede tal cual: el saldo
   * del club no cambia y no se cobra diferencia.
   *
   * Contadores/ranking: como ambos pertenecen al mismo club y B hereda el
   * estado de A, el neto de `registeredCount`/`confirmedCount`/ranking es CERO
   * (A −1, B +1), por lo que NO se tocan (evita contención innecesaria).
   *
   * NO valida fechas de inscripción ni estado del pago: el intercambio es un
   * cambio de última hora legítimo y procede siempre (el permiso lo controla la
   * capa UI: director del club o admin de asociación).
   *
   * No maneja consentimientos: eso lo orquesta el llamador (ver hook
   * useSwapRegistration → voidConsentBySwap del que sale + initConsent del que
   * entra), porque requiere los objetos Member/Person/Event.
   */
  async swap(params: {
    outgoingRegistrationId: string;
    incoming: { personId: string; personName: string; memberId: string };
    actor: { uid: string; name?: string };
    reason?: string;
  }): Promise<{ newRegistrationId: string; outgoingPersonId: string }> {
    const { outgoingRegistrationId, incoming, actor, reason } = params;

    const outgoing = await this.getById(outgoingRegistrationId);
    if (!outgoing) throw new Error("Inscripción a reemplazar no encontrada");
    if (!isActiveStatus(outgoing.status)) {
      throw new Error(
        "Solo se puede intercambiar una inscripción activa (pendiente, aprobada o en lista de espera)",
      );
    }
    if (incoming.personId === outgoing.personId) {
      throw new Error("El integrante que entra debe ser diferente al que sale");
    }
    if (!incoming.personId) {
      throw new Error("El integrante que entra no tiene perfil de persona");
    }

    const outgoingRef = doc(db, "eventRegistrations", outgoingRegistrationId);
    const incomingRegistrationId = `${outgoing.eventId}_${incoming.personId}`;
    const incomingRef = doc(db, "eventRegistrations", incomingRegistrationId);

    await withRetry(
      () =>
        runTransaction(db, async (tx) => {
          const [outSnap, inSnap] = await Promise.all([
            tx.get(outgoingRef),
            tx.get(incomingRef),
          ]);
          if (!outSnap.exists()) {
            throw new Error("Inscripción a reemplazar no encontrada");
          }
          const out = outSnap.data() as EventRegistration;
          if (!isActiveStatus(out.status)) {
            throw new Error("La inscripción a reemplazar ya no está activa");
          }

          // El integrante que entra no puede tener ya una inscripción activa.
          if (inSnap.exists()) {
            const inPrev = inSnap.data() as EventRegistration;
            if (isActiveStatus(inPrev.status)) {
              throw new Error(
                `${incoming.personName} ya está inscrito(a) en este evento`,
              );
            }
          }

          const now = serverTimestamp();

          // 1) El que SALE → cancelado, con traza del reemplazo.
          tx.update(outgoingRef, {
            status: "cancelled",
            swappedOutTo: incoming.personId,
            swappedAt: now,
            updatedAt: now,
            updatedBy: actor.uid,
          });

          // 2) El que ENTRA → hereda precio/pago/categoría/estado del que sale.
          const inheritedFields = {
            eventId: out.eventId,
            eventName: out.eventName,
            clubId: out.clubId,
            clubName: out.clubName,
            personId: incoming.personId,
            personName: incoming.personName,
            memberId: incoming.memberId,
            category: out.category,
            unitPrice: out.unitPrice,
            status: out.status,
            paymentStatus: out.paymentStatus,
            amountPaid: out.amountPaid,
            swappedInFrom: out.personId,
            swappedAt: now,
          };

          if (inSnap.exists()) {
            // Reactivar doc terminal preservando createdAt/createdBy.
            tx.update(incomingRef, {
              ...inheritedFields,
              registeredById: actor.uid,
              registeredByName: actor.name ?? "",
              registrationDate: now,
              // limpiar marcas de estados terminales previos
              rejectedAt: deleteField(),
              rejectedBy: deleteField(),
              rejectionReason: deleteField(),
              updatedAt: now,
              updatedBy: actor.uid,
            });
          } else {
            tx.set(incomingRef, {
              ...inheritedFields,
              id: incomingRegistrationId,
              registeredById: actor.uid,
              registeredByName: actor.name ?? "",
              registrationDate: now,
              createdAt: now,
              updatedAt: now,
              createdBy: actor.uid,
              updatedBy: actor.uid,
            });
          }

          // Sin cambios en contadores/ranking: neto cero (mismo club, B hereda
          // el estado de A).
        }),
      { label: "swapRegistration" },
    );

    // Auditoría (post-TX, no bloqueante para la respuesta).
    await eventRegistrationAuditService.log({
      registrationId: outgoingRegistrationId,
      eventId: outgoing.eventId,
      eventName: outgoing.eventName,
      clubId: outgoing.clubId,
      clubName: outgoing.clubName,
      personId: outgoing.personId,
      personName: outgoing.personName,
      action: "swap",
      changes: [
        { field: "status", before: outgoing.status, after: "cancelled" },
        { field: "swappedOutTo", before: null, after: incoming.personId },
      ],
      reason,
      performedBy: actor.uid,
      performedByName: actor.name,
    });
    await eventRegistrationAuditService.log({
      registrationId: incomingRegistrationId,
      eventId: outgoing.eventId,
      eventName: outgoing.eventName,
      clubId: outgoing.clubId,
      clubName: outgoing.clubName,
      personId: incoming.personId,
      personName: incoming.personName,
      action: "swap",
      changes: [
        { field: "swappedInFrom", before: null, after: outgoing.personId },
        { field: "unitPrice", before: null, after: outgoing.unitPrice },
        { field: "paymentStatus", before: null, after: outgoing.paymentStatus },
      ],
      reason,
      performedBy: actor.uid,
      performedByName: actor.name,
    });

    return {
      newRegistrationId: incomingRegistrationId,
      outgoingPersonId: outgoing.personId,
    };
  },

  // Verificar si una persona ya está inscrita en un evento
  async isPersonRegistered(
    eventId: string,
    personId: string,
  ): Promise<boolean> {
    const registrations = await getAll<EventRegistration>(
      "eventRegistrations",
      [
        where("eventId", "==", eventId),
        where("personId", "==", personId),
        where("status", "in", ["pending", "approved", "waitlist"]),
      ],
    );
    return registrations.length > 0;
  },

  // Obtener estadísticas de inscripción por evento
  async getEventStats(eventId: string): Promise<{
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    cancelled: number;
    waitlist: number;
    totalPaid: number;
    totalPending: number;
  }> {
    const registrations = await this.getByEvent(eventId);

    const stats = {
      total: registrations.length,
      pending: 0,
      approved: 0,
      rejected: 0,
      cancelled: 0,
      waitlist: 0,
      totalPaid: 0,
      totalPending: 0,
    };

    for (const reg of registrations) {
      stats[reg.status]++;
      if (reg.status === "approved" || reg.status === "pending") {
        stats.totalPaid += reg.amountPaid;
        stats.totalPending += reg.unitPrice - reg.amountPaid;
      }
    }

    return stats;
  },
};

// ==================== CLUB EVENT RANKING ====================

export const MIN_REGISTRATIONS_FOR_RANKING = 10;

export const clubEventRankingService = {
  async getByEvent(eventId: string): Promise<ClubEventRanking[]> {
    const rankings = await getAll<ClubEventRanking>("clubEventRankings", [
      where("eventId", "==", eventId),
    ]);

    // Derivar posiciones por completedAt (evita race condition de posición duplicada)
    rankings.sort((a, b) => {
      if (a.completedAt && b.completedAt) {
        return a.completedAt.toMillis() - b.completedAt.toMillis();
      }
      if (a.completedAt && !b.completedAt) return -1;
      if (!a.completedAt && b.completedAt) return 1;
      return (b.totalPayments ?? 0) - (a.totalPayments ?? 0);
    });

    let pos = 1;
    for (const r of rankings) {
      r.position = r.completedAt ? pos++ : undefined;
    }

    return rankings;
  },

  async getByClubAndEvent(
    clubId: string,
    eventId: string,
  ): Promise<ClubEventRanking | null> {
    const results = await getAll<ClubEventRanking>("clubEventRankings", [
      where("clubId", "==", clubId),
      where("eventId", "==", eventId),
    ]);
    const ranking = results[0] || null;
    if (!ranking || !ranking.completedAt) return ranking;

    // Derive position from all completed rankings for this event
    const allRankings = await getAll<ClubEventRanking>("clubEventRankings", [
      where("eventId", "==", eventId),
    ]);
    const completed = allRankings
      .filter((r) => r.completedAt)
      .sort((a, b) => a.completedAt!.toMillis() - b.completedAt!.toMillis());
    const pos = completed.findIndex((r) => r.clubId === clubId);
    ranking.position = pos >= 0 ? pos + 1 : undefined;

    return ranking;
  },

  /**
   * Recalcula el ranking de un club desde fuentes de verdad (queries).
   *
   * @deprecated USO RESTRINGIDO: solo para reconciliación / backfill manual.
   * El flujo normal (create registration / uploadPayment / updateStatus) ya
   * mantiene el ranking de forma atómica con incrementos en transacción.
   * Llamar este método introduce eventual consistency y puede pisar deltas
   * en vuelo. NO usar en flujos de UI.
   */
  async recalculateClubRanking(
    eventId: string,
    clubId: string,
    clubName: string,
    userId: string,
  ): Promise<ClubEventRanking> {
    const registrations = await eventRegistrationService.getByClubAndEvent(
      clubId,
      eventId,
    );
    const activeRegs = registrations.filter((r) => isActiveStatus(r.status));
    const payments = await eventPaymentService.getByClubAndEvent(
      clubId,
      eventId,
    );
    const totalPayments = payments.length;
    const totalPaymentAmount = payments.reduce((sum, p) => sum + p.amount, 0);

    // Usar ID determinístico para idempotencia.
    const rankingId = rankingDocId(eventId, clubId);
    const rankingRef = doc(db, "clubEventRankings", rankingId);

    return runTransaction(db, async (tx) => {
      const snap = await tx.get(rankingRef);
      const base = {
        totalRegistered: activeRegs.length,
        totalPayments,
        totalPaymentAmount,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      } as Record<string, unknown>;

      // El sello NO se escribe desde aquí: lo pone el servidor
      // (functions/src/ranking-seal.ts), que es el único que puede
      // comprobarlo contra los datos reales.
      if (snap.exists()) {
        const r = snap.data() as ClubEventRanking;
        tx.update(rankingRef, base);
        return {
          ...r,
          ...(base as Partial<ClubEventRanking>),
        } as ClubEventRanking;
      } else {
        const newDoc = {
          id: rankingId,
          eventId,
          clubId,
          clubName,
          ...base,
          createdAt: serverTimestamp(),
          createdBy: userId,
        };
        tx.set(rankingRef, newDoc);
        return { ...(newDoc as unknown as ClubEventRanking) };
      }
    });
  },
};

// ==================== EVENT PAYMENTS (POR CLUB) ====================

export const eventPaymentService = {
  async getById(id: string): Promise<EventPayment | null> {
    return getById<EventPayment>("eventPayments", id);
  },

  async getByEvent(eventId: string): Promise<EventPayment[]> {
    return getAll<EventPayment>("eventPayments", [
      where("eventId", "==", eventId),
      orderBy("uploadedAt", "desc"),
    ]);
  },

  async getByClubAndEvent(
    clubId: string,
    eventId: string,
  ): Promise<EventPayment[]> {
    return getAll<EventPayment>("eventPayments", [
      where("clubId", "==", clubId),
      where("eventId", "==", eventId),
      orderBy("uploadedAt", "desc"),
    ]);
  },

  async getByStatus(status: EventPaymentStatus): Promise<EventPayment[]> {
    return getAll<EventPayment>("eventPayments", [
      where("status", "==", status),
      orderBy("uploadedAt", "desc"),
    ]);
  },

  async getPendingByEvent(eventId: string): Promise<EventPayment[]> {
    return getAll<EventPayment>("eventPayments", [
      where("eventId", "==", eventId),
      where("status", "==", "pending"),
      orderBy("uploadedAt", "desc"),
    ]);
  },

  /**
   * Sube un pago (comprobante) a nivel de club para un evento.
   *
   * Garantías:
   *  - Bug 1+2+6: actualiza el ranking del club ATÓMICAMENTE en la misma TX
   *    que crea el pago, usando incrementos y serverTimestamp.
   *  - Bug 8: si el create del doc Firestore falla, el archivo encriptado
   *    subido a Storage se borra (no quedan recibos huérfanos).
   *  - 2026-08-06: el pago sólo SELLA el puesto en el ranking si el monto es
   *    creíble (al menos una inscripción al precio más barato del evento).
   *    Antes bastaba subir cualquier foto declarando $1 para quedarse con un
   *    puesto del top-10, que además es irreversible. El pago se registra
   *    igual: lo único que no ocurre es el sello.
   */
  async uploadPayment(
    eventId: string,
    eventName: string,
    clubId: string,
    clubName: string,
    amount: number,
    file: File,
    userId: string,
    userName: string,
    description?: string,
    notes?: string,
  ): Promise<string> {
    console.log("[uploadPayment] START", {
      eventId,
      clubId,
      amount,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      userId,
    });

    // 1) Subir archivo encriptado a Storage
    const folder = `receipts/${eventId}/${clubId}`;
    const paymentId = `${clubId}_${Date.now()}`;
    const upload = await uploadEncryptedReceipt(file, folder, paymentId);
    const { url, path, encryption } = upload;
    console.log("[uploadPayment] Storage upload OK", {
      path,
      url: url.slice(0, 80),
    });

    // 2) Crear doc + actualizar ranking en una sola TX. Si la TX falla,
    //    borrar el archivo subido para no dejar recibos huérfanos (bug 8).
    const paymentRef = doc(db, "eventPayments", paymentId);
    const rankingRef = doc(
      db,
      "clubEventRankings",
      rankingDocId(eventId, clubId),
    );

    try {
      await withRetry(
        () =>
          runTransaction(db, async (tx) => {
            // === LECTURAS ===
            const [paySnap, rankingSnap] = await Promise.all([
              tx.get(paymentRef),
              tx.get(rankingRef),
            ]);
            if (paySnap.exists()) {
              // colisión por timestamp idéntico — extremadamente improbable
              throw new Error("Pago duplicado, reintenta");
            }

            const paymentData: Record<string, unknown> = {
              id: paymentId,
              eventId,
              eventName,
              clubId,
              clubName,
              amount,
              receiptUrl: url,
              receiptPath: path,
              receiptContentType: file.type,
              receiptEncryption: encryption,
              status: "pending",
              uploadedBy: userId,
              uploadedByName: userName,
              uploadedAt: serverTimestamp(),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              createdBy: userId,
              updatedBy: userId,
              ...(description ? { description } : {}),
              ...(notes ? { notes } : {}),
            };
            tx.set(paymentRef, paymentData);

            // === RANKING ATÓMICO ===
            if (!rankingSnap.exists()) {
              tx.set(rankingRef, {
                id: rankingDocId(eventId, clubId),
                eventId,
                clubId,
                clubName,
                totalRegistered: 0,
                totalPayments: 1,
                totalPaymentAmount: amount,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                createdBy: userId,
                updatedBy: userId,
              });
            } else {
              const upd: Record<string, unknown> = {
                totalPayments: increment(1),
                totalPaymentAmount: increment(amount),
                updatedAt: serverTimestamp(),
                updatedBy: userId,
              };
              // El sello (completedAt) lo pone el servidor: ver
              // functions/src/ranking-seal.ts. El cliente sólo lleva los
              // totales para que la UI reaccione al instante.
              tx.update(rankingRef, upd);
            }
          }),
        { label: "uploadPayment" },
      );
      console.log("[uploadPayment] Firestore TX OK", { id: paymentId });
    } catch (err) {
      console.error(
        "[uploadPayment] Firestore TX FAILED — rolling back Storage",
        err,
      );
      // Bug 8: rollback del archivo encriptado
      try {
        await deleteFile(path);
        console.log("[uploadPayment] Storage rollback OK", { path });
      } catch (rollbackErr) {
        console.error(
          "[uploadPayment] Storage rollback FAILED (orphan file!)",
          { path },
          rollbackErr,
        );
      }
      throw err;
    }

    return paymentId;
  },

  /**
   * Valida (aprueba o rechaza) un pago. Solo la asociación puede hacerlo.
   *
   * Nota: NO modifica el ranking. El ranking cuenta pagos existentes
   * independiente de su validación (la regla del negocio es "≥1 pago
   * subido" para clasificar). Cambiar este comportamiento requiere
   * además ajustar uploadPayment y la migración de datos.
   */
  async validatePayment(
    paymentId: string,
    status: "approved" | "rejected" | "pending",
    validatedBy: string,
    validatedByName: string,
    rejectionReason?: string,
    /**
     * Si se pasa, el admin está corrigiendo el monto declarado por el club
     * antes de aprobar. Se preserva el valor original en `originalAmount` y
     * se deja huella de quién/cuándo lo ajustó.
     */
    newAmount?: number,
  ): Promise<void> {
    if (status === "pending") {
      // Revertir validación: dejar el pago como pendiente y limpiar datos del revisor.
      const updateData: Record<string, unknown> = {
        status: "pending",
        validatedBy: deleteField(),
        validatedByName: deleteField(),
        validatedAt: deleteField(),
        rejectionReason: deleteField(),
      };
      await update<EventPayment>(
        "eventPayments",
        paymentId,
        updateData as Partial<EventPayment>,
      );
      return;
    }
    const updateData: Partial<EventPayment> = {
      status,
      validatedBy,
      validatedByName,
      validatedAt: Timestamp.now(),
    };
    if (status === "rejected" && rejectionReason) {
      updateData.rejectionReason = rejectionReason;
    }
    if (
      typeof newAmount === "number" &&
      Number.isFinite(newAmount) &&
      newAmount >= 0
    ) {
      // Solo aplicar si el monto cambia respecto al actual.
      const current = await getById<EventPayment>("eventPayments", paymentId);
      if (current && current.amount !== newAmount) {
        updateData.amount = newAmount;
        updateData.originalAmount = current.originalAmount ?? current.amount;
        updateData.amountAdjustedBy = validatedBy;
        updateData.amountAdjustedByName = validatedByName;
        updateData.amountAdjustedAt = Timestamp.now();
      }
    }
    await update<EventPayment>("eventPayments", paymentId, updateData);
  },

  /**
   * Registra un pago en efectivo. Solo lo debe llamar un admin (organizador
   * de la asociación) que recibe el dinero en mano durante el evento.
   *
   * Diferencias con `uploadPayment`:
   *  - No sube archivo (no hay comprobante).
   *  - Queda con status="approved" y validado por el propio admin.
   *  - paymentMethod="cash" para distinguirlo en el UI.
   *  - Actualiza el ranking del club ATÓMICAMENTE (igual que uploadPayment).
   *
   * No se hace storage, así que no hay riesgo de archivos huérfanos.
   */
  async recordCashPayment(
    eventId: string,
    eventName: string,
    clubId: string,
    clubName: string,
    amount: number,
    userId: string,
    userName: string,
    description?: string,
    notes?: string,
  ): Promise<string> {
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error("El monto debe ser mayor a 0");
    }

    const paymentId = `${clubId}_cash_${Date.now()}`;
    const paymentRef = doc(db, "eventPayments", paymentId);
    const rankingRef = doc(
      db,
      "clubEventRankings",
      rankingDocId(eventId, clubId),
    );

    await withRetry(
      () =>
        runTransaction(db, async (tx) => {
          const [paySnap, rankingSnap] = await Promise.all([
            tx.get(paymentRef),
            tx.get(rankingRef),
          ]);
          if (paySnap.exists()) {
            throw new Error("Pago duplicado, reintenta");
          }

          const paymentData: Record<string, unknown> = {
            id: paymentId,
            eventId,
            eventName,
            clubId,
            clubName,
            amount,
            // Sin comprobante: usamos strings vacíos para satisfacer el tipo y
            // las rules (que solo exigen que no cambien en updates).
            receiptUrl: "",
            receiptPath: "",
            receiptContentType: "",
            paymentMethod: "cash",
            // Aprobado de entrada: el admin es quien recibe el efectivo.
            status: "approved",
            validatedBy: userId,
            validatedByName: userName,
            validatedAt: serverTimestamp(),
            uploadedBy: userId,
            uploadedByName: userName,
            uploadedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: userId,
            updatedBy: userId,
            ...(description ? { description } : {}),
            ...(notes ? { notes } : {}),
          };
          tx.set(paymentRef, paymentData);

          if (!rankingSnap.exists()) {
            tx.set(rankingRef, {
              id: rankingDocId(eventId, clubId),
              eventId,
              clubId,
              clubName,
              totalRegistered: 0,
              totalPayments: 1,
              totalPaymentAmount: amount,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              createdBy: userId,
              updatedBy: userId,
            });
          } else {
            const upd: Record<string, unknown> = {
              totalPayments: increment(1),
              totalPaymentAmount: increment(amount),
              updatedAt: serverTimestamp(),
              updatedBy: userId,
            };
            // El sello lo pone el servidor (functions/src/ranking-seal.ts).
            tx.update(rankingRef, upd);
          }
        }),
      { label: "recordCashPayment" },
    );

    return paymentId;
  },
};

// ==================== RECEIPT UPLOAD (LEGACY — DEPRECATED) ====================

/** @deprecated Usar eventPaymentService.uploadPayment */
export const receiptService = {
  async uploadReceipt(
    registrationId: string,
    file: File,
    userId: string,
  ): Promise<{ url: string; path: string }> {
    const registration = await eventRegistrationService.getById(registrationId);
    if (!registration) throw new Error("Inscripción no encontrada");

    const folder = `receipts/${registration.eventId}/${registration.clubId}`;
    const { url, path, encryption } = await uploadEncryptedReceipt(
      file,
      folder,
      registrationId,
    );

    await eventRegistrationService.update(registrationId, {
      receiptUrl: url,
      receiptPath: path,
      receiptUploadedAt: Timestamp.now(),
      receiptEncryption: encryption,
      receiptContentType: file.type,
      paymentStatus: "pending",
    });

    await clubEventRankingService.recalculateClubRanking(
      registration.eventId,
      registration.clubId,
      registration.clubName,
      userId,
    );

    return { url, path };
  },
};

// ==================== REALTIME SUBSCRIPTIONS ====================

/** Suscripción en tiempo real a un evento */
export function subscribeToEvent(
  eventId: string,
  callback: (event: Event | null) => void,
): () => void {
  return subscribeToDocument<Event>("events", eventId, callback);
}

/** Suscripción en tiempo real a las inscripciones de un evento */
export function subscribeToEventRegistrations(
  eventId: string,
  callback: (registrations: EventRegistration[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<EventRegistration>(
    "eventRegistrations",
    callback,
    [where("eventId", "==", eventId), orderBy("createdAt", "desc")],
    onError,
  );
}

/** Suscripción en tiempo real a todos los pagos de un evento */
export function subscribeToEventPayments(
  eventId: string,
  callback: (payments: EventPayment[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<EventPayment>(
    "eventPayments",
    callback,
    [where("eventId", "==", eventId), orderBy("uploadedAt", "desc")],
    onError,
  );
}

/** Suscripción en tiempo real a los pagos de un club para un evento */
export function subscribeToClubEventPayments(
  clubId: string,
  eventId: string,
  callback: (payments: EventPayment[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<EventPayment>(
    "eventPayments",
    callback,
    [
      where("clubId", "==", clubId),
      where("eventId", "==", eventId),
      orderBy("uploadedAt", "desc"),
    ],
    onError,
  );
}

/** Suscripción en tiempo real a TODOS los pagos de eventos de un club */
export function subscribeToClubEventPaymentsAll(
  clubId: string,
  callback: (payments: EventPayment[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<EventPayment>(
    "eventPayments",
    callback,
    [where("clubId", "==", clubId)],
    onError,
  );
}

/** Suscripción en tiempo real al ranking de todos los clubes de un evento */
export function subscribeToEventRankings(
  eventId: string,
  callback: (rankings: ClubEventRanking[]) => void,
): () => void {
  return subscribeToCollection<ClubEventRanking>(
    "clubEventRankings",
    (rawRankings) => {
      // Derivar posiciones por completedAt
      const rankings = [...rawRankings].sort((a, b) => {
        if (a.completedAt && b.completedAt) {
          const aMs =
            a.completedAt instanceof Date
              ? a.completedAt.getTime()
              : (a.completedAt as unknown as { seconds: number }).seconds *
                1000;
          const bMs =
            b.completedAt instanceof Date
              ? b.completedAt.getTime()
              : (b.completedAt as unknown as { seconds: number }).seconds *
                1000;
          return aMs - bMs;
        }
        if (a.completedAt && !b.completedAt) return -1;
        if (!a.completedAt && b.completedAt) return 1;
        return (b.totalPayments ?? 0) - (a.totalPayments ?? 0);
      });
      let pos = 1;
      for (const r of rankings) {
        r.position = r.completedAt ? pos++ : undefined;
      }
      callback(rankings);
    },
    [where("eventId", "==", eventId)],
  );
}

/** Suscripción en tiempo real al ranking de un club para un evento */
export function subscribeToClubEventRanking(
  clubId: string,
  eventId: string,
  callback: (ranking: ClubEventRanking | null) => void,
): () => void {
  // Suscribirse a todos los rankings del evento para poder calcular posición
  return subscribeToCollection<ClubEventRanking>(
    "clubEventRankings",
    (rawRankings) => {
      const myRanking = rawRankings.find((r) => r.clubId === clubId) ?? null;
      if (!myRanking) {
        callback(null);
        return;
      }

      if (myRanking.completedAt) {
        const completed = rawRankings
          .filter((r) => r.completedAt)
          .sort((a, b) => {
            const aMs =
              a.completedAt instanceof Date
                ? a.completedAt.getTime()
                : (a.completedAt as unknown as { seconds: number }).seconds *
                  1000;
            const bMs =
              b.completedAt instanceof Date
                ? b.completedAt.getTime()
                : (b.completedAt as unknown as { seconds: number }).seconds *
                  1000;
            return aMs - bMs;
          });
        const pos = completed.findIndex((r) => r.clubId === clubId);
        myRanking.position = pos >= 0 ? pos + 1 : undefined;
      }
      callback(myRanking);
    },
    [where("eventId", "==", eventId)],
  );
}

// ==================== HELPERS ====================

export function getEventStatusLabel(status: EventStatus): string {
  const labels: Record<EventStatus, string> = {
    draft: "Borrador",
    published: "Publicado",
    registration_closed: "Inscripciones Cerradas",
    in_progress: "En Curso",
    completed: "Finalizado",
    cancelled: "Cancelado",
  };
  return labels[status];
}

export function getEventStatusColor(status: EventStatus): string {
  const colors: Record<EventStatus, string> = {
    draft: "bg-gray-100 text-gray-800",
    published: "bg-green-100 text-green-800",
    registration_closed: "bg-yellow-100 text-yellow-800",
    in_progress: "bg-blue-100 text-blue-800",
    completed: "bg-purple-100 text-purple-800",
    cancelled: "bg-red-100 text-red-800",
  };
  return colors[status];
}

export function getRegistrationStatusLabel(status: RegistrationStatus): string {
  const labels: Record<RegistrationStatus, string> = {
    pending: "Pendiente",
    approved: "Aprobado",
    rejected: "Rechazado",
    cancelled: "Cancelado",
    waitlist: "Lista de Espera",
  };
  return labels[status];
}

export function getRegistrationStatusColor(status: RegistrationStatus): string {
  const colors: Record<RegistrationStatus, string> = {
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    cancelled: "bg-gray-100 text-gray-800",
    waitlist: "bg-orange-100 text-orange-800",
  };
  return colors[status];
}

// ==================== ADDITIONAL REALTIME SUBSCRIPTIONS ====================

/** Suscripción en tiempo real a todos los eventos */
export function subscribeToEvents(
  callback: (events: Event[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Event>(
    "events",
    callback,
    [orderBy("startDate", "desc")],
    onError,
  );
}

/** Suscripción en tiempo real a próximos eventos */
export function subscribeToUpcomingEvents(
  callback: (events: Event[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const now = Timestamp.now();
  return subscribeToCollection<Event>(
    "events",
    callback,
    [
      where("status", "in", ["published", "registration_closed"]),
      where("startDate", ">=", now),
      orderBy("startDate", "asc"),
    ],
    onError,
  );
}

/** Suscripción en tiempo real a eventos por estado */
export function subscribeToEventsByStatus(
  status: EventStatus,
  callback: (events: Event[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Event>(
    "events",
    callback,
    [where("status", "==", status), orderBy("startDate", "desc")],
    onError,
  );
}

/** Suscripción en tiempo real a inscripciones de un club */
export function subscribeToClubRegistrations(
  clubId: string,
  callback: (registrations: EventRegistration[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<EventRegistration>(
    "eventRegistrations",
    callback,
    [where("clubId", "==", clubId), orderBy("registrationDate", "desc")],
    onError,
  );
}

/** Suscripción en tiempo real a pagos pendientes de un evento */
export function subscribeToPendingPaymentsByEvent(
  eventId: string,
  callback: (payments: EventPayment[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<EventPayment>(
    "eventPayments",
    callback,
    [
      where("eventId", "==", eventId),
      where("status", "==", "pending"),
      orderBy("uploadedAt", "desc"),
    ],
    onError,
  );
}

/** Suscripción en tiempo real a eventos publicados */
export function subscribeToPublishedEvents(
  callback: (events: Event[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Event>(
    "events",
    callback,
    [
      where("status", "in", [
        "published",
        "registration_closed",
        "in_progress",
      ]),
      orderBy("startDate", "desc"),
    ],
    onError,
  );
}

/** Suscripción en tiempo real a eventos por organizador */
export function subscribeToEventsByOrganizer(
  organizerId: string,
  callback: (events: Event[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Event>(
    "events",
    callback,
    [where("organizerId", "==", organizerId), orderBy("startDate", "desc")],
    onError,
  );
}

/** Suscripción en tiempo real a eventos por nivel */
export function subscribeToEventsByLevel(
  level: Event["level"],
  callback: (events: Event[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Event>(
    "events",
    callback,
    [where("level", "==", level), orderBy("startDate", "desc")],
    onError,
  );
}

/** Suscripción en tiempo real a registros por evento y estado */
export function subscribeToEventRegistrationsByStatus(
  eventId: string,
  status: RegistrationStatus,
  callback: (registrations: EventRegistration[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<EventRegistration>(
    "eventRegistrations",
    callback,
    [
      where("eventId", "==", eventId),
      where("status", "==", status),
      orderBy("registrationDate", "desc"),
    ],
    onError,
  );
}

/** Suscripción en tiempo real a registros por club y evento */
export function subscribeToClubEventRegistrations(
  clubId: string,
  eventId: string,
  callback: (registrations: EventRegistration[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<EventRegistration>(
    "eventRegistrations",
    callback,
    [
      where("clubId", "==", clubId),
      where("eventId", "==", eventId),
      orderBy("registrationDate", "desc"),
    ],
    onError,
  );
}

/** Suscripción en tiempo real a registros por persona */
export function subscribeToPersonRegistrations(
  personId: string,
  callback: (registrations: EventRegistration[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<EventRegistration>(
    "eventRegistrations",
    callback,
    [where("personId", "==", personId), orderBy("registrationDate", "desc")],
    onError,
  );
}

// ==================== EVENT REGISTRATION AUDIT ====================

export const eventRegistrationAuditService = {
  async log(entry: {
    registrationId: string;
    eventId: string;
    eventName?: string;
    clubId: string;
    clubName?: string;
    personId: string;
    personName?: string;
    action: RegistrationAuditAction;
    changes: RegistrationAuditChange[];
    reason?: string;
    performedBy: string;
    performedByName?: string;
  }): Promise<string> {
    const data: Omit<
      EventRegistrationAudit,
      "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
    > = {
      registrationId: entry.registrationId,
      eventId: entry.eventId,
      ...(entry.eventName ? { eventName: entry.eventName } : {}),
      clubId: entry.clubId,
      ...(entry.clubName ? { clubName: entry.clubName } : {}),
      personId: entry.personId,
      ...(entry.personName ? { personName: entry.personName } : {}),
      action: entry.action,
      changes: entry.changes,
      ...(entry.reason ? { reason: entry.reason } : {}),
      performedBy: entry.performedBy,
      ...(entry.performedByName
        ? { performedByName: entry.performedByName }
        : {}),
      performedAt: Timestamp.now(),
    };
    return create<EventRegistrationAudit>(
      "eventRegistrationAudit",
      data,
      entry.performedBy,
    );
  },

  async getByRegistration(
    registrationId: string,
  ): Promise<EventRegistrationAudit[]> {
    return getAll<EventRegistrationAudit>("eventRegistrationAudit", [
      where("registrationId", "==", registrationId),
      orderBy("performedAt", "desc"),
    ]);
  },

  async getByEvent(eventId: string): Promise<EventRegistrationAudit[]> {
    return getAll<EventRegistrationAudit>("eventRegistrationAudit", [
      where("eventId", "==", eventId),
      orderBy("performedAt", "desc"),
    ]);
  },
};

// ==================== ADMIN OPS (asociación) ====================
//
// Operaciones poderosas reservadas para admin de asociación / unión.
// Cada cambio queda registrado en `eventRegistrationAudit`.
//

const EDITABLE_FIELDS: (keyof EventRegistration)[] = [
  "category",
  "unitPrice",
  "paymentStatus",
  "notes",
  "medicalNotes",
  "dietaryRestrictions",
];

function diffFields<T extends Record<string, unknown>>(
  before: T,
  after: Partial<T>,
  fields: (keyof T)[],
): RegistrationAuditChange[] {
  const changes: RegistrationAuditChange[] = [];
  for (const f of fields) {
    if (!(f in after)) continue;
    const b = before[f];
    const a = after[f];
    // Comparación simple — suficiente para campos primitivos.
    if (JSON.stringify(b) !== JSON.stringify(a)) {
      changes.push({ field: String(f), before: b, after: a });
    }
  }
  return changes;
}

export const adminRegistrationService = {
  /**
   * Edita campos básicos (categoría, precio unitario, notas).
   */
  async editFields(
    registrationId: string,
    patch: Partial<
      Pick<
        EventRegistration,
        | "category"
        | "unitPrice"
        | "paymentStatus"
        | "notes"
        | "medicalNotes"
        | "dietaryRestrictions"
      >
    >,
    actor: { uid: string; name?: string },
    reason?: string,
  ): Promise<void> {
    const reg = await eventRegistrationService.getById(registrationId);
    if (!reg) throw new Error("Inscripción no encontrada");

    const changes = diffFields(
      reg as unknown as Record<string, unknown>,
      patch as unknown as Record<string, unknown>,
      EDITABLE_FIELDS as unknown as (keyof Record<string, unknown>)[],
    );
    if (changes.length === 0) return;

    await update<EventRegistration>(
      "eventRegistrations",
      registrationId,
      patch,
    );

    await eventRegistrationAuditService.log({
      registrationId,
      eventId: reg.eventId,
      eventName: reg.eventName,
      clubId: reg.clubId,
      clubName: reg.clubName,
      personId: reg.personId,
      personName: reg.personName,
      action: "edit_fields",
      changes,
      reason,
      performedBy: actor.uid,
      performedByName: actor.name,
    });
  },

  /**
   * Transfiere la inscripción a otro club. Recalcula rankings de ambos clubes.
   */
  async transferToClub(
    registrationId: string,
    targetClubId: string,
    targetClubName: string,
    actor: { uid: string; name?: string },
    reason?: string,
  ): Promise<void> {
    const reg = await eventRegistrationService.getById(registrationId);
    if (!reg) throw new Error("Inscripción no encontrada");
    if (reg.clubId === targetClubId) return;

    const previousClubId = reg.clubId;
    const previousClubName = reg.clubName;

    await update<EventRegistration>("eventRegistrations", registrationId, {
      clubId: targetClubId,
      clubName: targetClubName,
    });

    await eventRegistrationAuditService.log({
      registrationId,
      eventId: reg.eventId,
      eventName: reg.eventName,
      clubId: targetClubId,
      clubName: targetClubName,
      personId: reg.personId,
      personName: reg.personName,
      action: "transfer_club",
      changes: [
        { field: "clubId", before: previousClubId, after: targetClubId },
        { field: "clubName", before: previousClubName, after: targetClubName },
      ],
      reason,
      performedBy: actor.uid,
      performedByName: actor.name,
    });

    // Recalcular ranking de ambos clubes (non-blocking)
    try {
      await clubEventRankingService.recalculateClubRanking(
        reg.eventId,
        previousClubId,
        previousClubName,
        actor.uid,
      );
      await clubEventRankingService.recalculateClubRanking(
        reg.eventId,
        targetClubId,
        targetClubName,
        actor.uid,
      );
    } catch (err) {
      console.error("[transferToClub] Ranking recalc FAILED", err);
    }
  },

  /**
   * Forzar cambio de estado (admin override). Reusa updateStatus() para
   * mantener contadores y ranking sincronizados.
   */
  async forceStatus(
    registrationId: string,
    status: RegistrationStatus,
    actor: { uid: string; name?: string },
    reason?: string,
  ): Promise<void> {
    const reg = await eventRegistrationService.getById(registrationId);
    if (!reg) throw new Error("Inscripción no encontrada");
    if (reg.status === status) return;

    const previousStatus = reg.status;

    await eventRegistrationService.updateStatus(
      registrationId,
      status,
      actor.uid,
      reason,
    );

    await eventRegistrationAuditService.log({
      registrationId,
      eventId: reg.eventId,
      eventName: reg.eventName,
      clubId: reg.clubId,
      clubName: reg.clubName,
      personId: reg.personId,
      personName: reg.personName,
      action: "force_status",
      changes: [{ field: "status", before: previousStatus, after: status }],
      reason,
      performedBy: actor.uid,
      performedByName: actor.name,
    });
  },

  /**
   * Re-inscribe a una persona cuya inscripción está cancelled / rejected,
   * dejándola en estado `pending` y conservando su historial.
   */
  async reactivate(
    registrationId: string,
    actor: { uid: string; name?: string },
    reason?: string,
  ): Promise<void> {
    const reg = await eventRegistrationService.getById(registrationId);
    if (!reg) throw new Error("Inscripción no encontrada");
    if (!["cancelled", "rejected"].includes(reg.status)) {
      throw new Error(
        "Solo se pueden reactivar inscripciones canceladas o rechazadas",
      );
    }
    await this.forceStatus(registrationId, "pending", actor, reason);
    await eventRegistrationAuditService.log({
      registrationId,
      eventId: reg.eventId,
      eventName: reg.eventName,
      clubId: reg.clubId,
      clubName: reg.clubName,
      personId: reg.personId,
      personName: reg.personName,
      action: "reactivate",
      changes: [{ field: "status", before: reg.status, after: "pending" }],
      reason,
      performedBy: actor.uid,
      performedByName: actor.name,
    });
  },

  /**
   * Elimina la inscripción definitivamente. Mantiene el log de auditoría como
   * registro histórico. NO borra los pagos del club (porque son por club, no
   * por inscripción).
   */
  async hardDelete(
    registrationId: string,
    actor: { uid: string; name?: string },
    reason?: string,
  ): Promise<void> {
    const reg = await eventRegistrationService.getById(registrationId);
    if (!reg) throw new Error("Inscripción no encontrada");

    // Los contadores del evento los descuenta `onEventRegistrationWrite` al
    // ver desaparecer el documento (antes se hacía aquí con un read+write que
    // las rules rechazaban en los eventos de la Asociación).

    // Log antes de borrar (para preservar el snapshot completo)
    await eventRegistrationAuditService.log({
      registrationId,
      eventId: reg.eventId,
      eventName: reg.eventName,
      clubId: reg.clubId,
      clubName: reg.clubName,
      personId: reg.personId,
      personName: reg.personName,
      action: "delete",
      changes: [{ field: "_snapshot", before: reg, after: null }],
      reason,
      performedBy: actor.uid,
      performedByName: actor.name,
    });

    await remove("eventRegistrations", registrationId);

    // Recalcular ranking del club tras eliminar (non-blocking)
    try {
      await clubEventRankingService.recalculateClubRanking(
        reg.eventId,
        reg.clubId,
        reg.clubName,
        actor.uid,
      );
    } catch (err) {
      console.error("[hardDelete] Ranking recalc FAILED", err);
    }
  },
};
